/**
 * People (identity) resource API — authenticated calls to `/api/people/api/v1.0/...`.
 * Dev proxy strips `/api/people` and forwards to the People Django service.
 */

import { useShellStore } from "../store";

const PEOPLE_API_PREFIX = "/api/v1.0";

/** Browser path: `/api/people` + API segment (must start with `/api/v1.0/...`). */
export function peopleResourceUrl(apiPath: string): string {
  const p = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
  return `/api/people${p}`;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  bio: string;
  phone: string;
  /** Empty string when unset — never null (see #1432 empty-value convention). */
  company: string;
  /** Empty string when unset — never null (see #1432 empty-value convention). */
  job_title: string;
  avatar_url: string | null;
  language: string;
  timezone: string;
  created_at: string;
}

export type ProfilePatchPayload = Partial<
  Pick<UserProfile, "name" | "bio" | "phone" | "company" | "job_title">
>;

export class PeopleApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "PeopleApiError";
  }
}

async function parseErrorBody(res: Response): Promise<{
  message: string;
  fieldErrors?: Record<string, string[]>;
}> {
  try {
    const data = (await res.json()) as Record<string, unknown>;
    const fieldErrors: Record<string, string[]> = {};
    let message = `HTTP ${res.status}`;

    if (typeof data.detail === "string") {
      message = data.detail;
    } else if (Array.isArray(data.detail)) {
      message = data.detail.map(String).join(" ");
    }

    for (const [key, val] of Object.entries(data)) {
      if (key === "detail") continue;
      if (Array.isArray(val) && val.every((v) => typeof v === "string")) {
        fieldErrors[key] = val as string[];
      } else if (typeof val === "string") {
        fieldErrors[key] = [val];
      }
    }

    if (Object.keys(fieldErrors).length > 0 && message === `HTTP ${res.status}`) {
      message = Object.entries(fieldErrors)
        .map(([k, msgs]) => `${k}: ${msgs.join(" ")}`)
        .join("; ");
    }

    return { message, fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined };
  } catch {
    return { message: `HTTP ${res.status}: ${res.statusText}` };
  }
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await useShellStore.getState().getValidAccessToken();
  if (!token) {
    throw new PeopleApiError("Not authenticated", 401);
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  return fetch(peopleResourceUrl(path), {
    ...init,
    headers,
  });
}

export async function fetchMyProfile(): Promise<UserProfile> {
  const res = await authFetch(`${PEOPLE_API_PREFIX}/users/me/`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const { message, fieldErrors } = await parseErrorBody(res);
    throw new PeopleApiError(message, res.status, fieldErrors);
  }

  return res.json() as Promise<UserProfile>;
}

export async function updateMyProfile(payload: ProfilePatchPayload): Promise<UserProfile> {
  const res = await authFetch(`${PEOPLE_API_PREFIX}/users/me/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const { message, fieldErrors } = await parseErrorBody(res);
    throw new PeopleApiError(message, res.status, fieldErrors);
  }

  return res.json() as Promise<UserProfile>;
}

export async function uploadAvatar(file: File | Blob, filename = "avatar.jpg"): Promise<{
  avatar_url: string;
}> {
  const token = await useShellStore.getState().getValidAccessToken();
  if (!token) {
    throw new PeopleApiError("Not authenticated", 401);
  }

  const formData = new FormData();
  formData.append("avatar", file, filename);

  const res = await fetch(peopleResourceUrl(`${PEOPLE_API_PREFIX}/users/me/avatar/`), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const { message, fieldErrors } = await parseErrorBody(res);
    throw new PeopleApiError(message, res.status, fieldErrors);
  }

  return res.json() as Promise<{ avatar_url: string }>;
}

export async function deleteAvatar(): Promise<void> {
  const res = await authFetch(`${PEOPLE_API_PREFIX}/users/me/avatar/`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok && res.status !== 204) {
    const { message, fieldErrors } = await parseErrorBody(res);
    throw new PeopleApiError(message, res.status, fieldErrors);
  }
}
